import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ReceiptText, Server, RadioTower, Search, Scale, Sparkles, FolderOpen, Plus, Clock, Users, Lock, CreditCard, Presentation, Gauge } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { fmtDate, fmtNum, TOOL_META } from "@/lib/format";

const TOOLS = [
  {
    key: "pajak",
    icon: ReceiptText,
    title: "Kalkulator Pajak",
    desc: "PPh 21 (TER 2024 + rekonsiliasi Desember), PPh 23 dan PPh Final 4 ayat (2). Metode gross, gross-up, dan net.",
    tags: ["PPh 21", "PPh 23", "PPh 4(2)"],
  },
  {
    key: "hosting",
    icon: Server,
    title: "COGS Hosting / VPS",
    desc: "Hitung COGS paket dari biaya hardware, listrik, DC, rak, bandwidth & IP. Kapasitas per server dan rekomendasi paket.",
    tags: ["11 server", "VPS", "Shared"],
  },
  {
    key: "isp",
    icon: RadioTower,
    title: "COGS Jaringan ISP",
    desc: "Konversi CAPEX & OPEX jaringan menjadi COGS per paket internet di wilayah existing maupun wilayah baru.",
    tags: ["FTTH", "Break-even", "Proyeksi"],
  },
  {
    key: "ekualizer",
    icon: Scale,
    title: "Ekualizer Pajak Multi-PT",
    desc: "Geser pendapatan & beban antar PT dalam grup lalu simulasikan efek PPh Badan, PPN, PPh 23 dan transfer pricing.",
    tags: ["PPh Badan", "PPN", "Transfer pricing"],
    noCount: true,
    adminOnly: true,
    href: "/tools/ekualizer",
  },
  {
    key: "kartu_kredit",
    icon: CreditCard,
    title: "Tagihan Kartu Kredit",
    desc: "Tarik e-statement dari Gmail atau upload banyak PDF, dirangkum AI per vendor & kategori, lalu buat PDF reimburse ke perusahaan.",
    tags: ["Gmail", "AI", "Reimburse PDF"],
    adminOnly: true,
    href: "/tools/kartu-kredit",
  },
  {
    key: "meeting",
    icon: Presentation,
    title: "Meeting Eddy",
    desc: "Slide Monday Briefing & Kamisan dengan gambar AI, papan tulis bulatan & panah relasi, serta voting dengan link publik.",
    tags: ["Slide AI", "Papan tulis", "Voting"],
    adminOnly: true,
    href: "/tools/meeting",
  },
  {
    key: "scraper",
    icon: Search,
    title: "Scraping Kontak LinkedIn",
    desc: "Temukan kontak bisnis berdasarkan jabatan, perusahaan/industri, dan wilayah. Baris dengan email atau WA/HP ditandai otomatis.",
    tags: ["Admin", "Apollo.io", "RapidAPI"],
    adminOnly: true,
    href: "/tools/scraper",
  },
  {
    key: "monitor_ai",
    icon: Gauge,
    title: "Monitor Token AI",
    desc: "Pantau saldo Emergent Universal Key, pemakaian token per tool, dan prediksi biaya bulanan Rupiah (AI + domain + server).",
    tags: ["Saldo", "Prediksi IDR", "Breakdown"],
    adminOnly: true,
    href: "/tools/monitor-ai",
  },
];

const ICONS = { pajak: ReceiptText, hosting: Server, isp: RadioTower, scraper: Search, ekualizer: Scale, kartu_kredit: CreditCard, meeting: Presentation, monitor_ai: Gauge };

export default function DashboardPage() {
  const { user, quota } = useAuth();
  const nav = useNavigate();
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    api
      .get("/calculations", { params: { scope: "all", limit: 50 } })
      .then(({ data }) => setRecent(data))
      .catch(() => setRecent([]));
  }, []);

  const counts = (recent || []).reduce((a, c) => ({ ...a, [c.tool]: (a[c.tool] || 0) + 1 }), {});
  const shared = (recent || []).filter((c) => c.permission !== "owner").length;
  const pct = quota?.limit ? Math.min(100, (quota.used / quota.limit) * 100) : 0;
  const hour = new Date().getHours();
  const greet = hour < 11 ? "Selamat pagi" : hour < 15 ? "Selamat siang" : hour < 19 ? "Selamat sore" : "Selamat malam";

  return (
    <div className="max-w-[1400px] mx-auto space-y-8">
      <section className="relative overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-card elev-1 fade-up">
        <img
          src="https://images.unsplash.com/photo-1645290099737-23f17b0b7dd8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200"
          alt=""
          className="absolute right-0 top-0 h-full w-[38%] object-cover opacity-30 hidden md:block"
          style={{ maskImage: "linear-gradient(90deg, transparent, black 60%)", WebkitMaskImage: "linear-gradient(90deg, transparent, black 60%)" }}
        />
        <div className="relative p-6 sm:p-8 md:max-w-[65%]">
          <div className="eyebrow">Workspace</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2" data-testid="dashboard-greeting">
            {greet}, <span className="text-primary">{user?.display_name || user?.email?.split("@")[0]}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-xl">
            Pilih tool untuk mulai menghitung. Semua perhitungan bisa disimpan, dibagikan ke rekan, dan dianalisa dengan AI.
          </p>
          <div className="mt-6 flex flex-wrap gap-6">
            <Stat label="Perhitungan saya" value={recent ? (recent.length - shared).toString() : "–"} testid="dashboard-stat-mine" />
            <Stat label="Dibagikan ke saya" value={recent ? shared.toString() : "–"} testid="dashboard-stat-shared" />
            <Stat label="Token AI terpakai" value={quota ? `${fmtNum(quota.used)}` : "–"} testid="dashboard-stat-tokens" />
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="eyebrow">Tools</div>
            <h2 className="text-lg font-semibold mt-1">Kalkulator bisnis</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {TOOLS.filter((t) => !t.adminOnly || user?.role === "admin").map((t, i) => (
            <button
              key={t.key}
              onClick={() => nav(t.href || TOOL_META[t.key].path)}
              className="press card-hover text-left rounded-xl border border-border bg-card p-5 elev-1 group fade-up flex flex-col"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`dashboard-tool-card-${t.key}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_9%)] flex items-center justify-center">
                  <t.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  {t.adminOnly && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] bg-[hsl(43_30%_8%)] px-2 py-0.5 text-[10px] text-primary"
                      data-testid={`dashboard-tool-locked-${t.key}`}
                    >
                      <Lock className="h-3 w-3" /> Admin only
                    </span>
                  )}
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                </div>
              </div>
              <h3 className="font-display text-xl mt-4">{t.title}</h3>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">{t.desc}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {t.tags.map((x) => (
                  <Badge key={x} variant="outline" className="border-border text-[10px] text-[hsl(45_15%_78%)]">
                    {x}
                  </Badge>
                ))}
                {!t.adminOnly && !t.noCount && (
                  <Badge variant="outline" className="border-[color:var(--hairline)] text-[10px] text-primary ml-auto num">
                    {counts[t.key] || 0} tersimpan
                  </Badge>
                )}
              </div>
            </button>
          ))}
          <div className="rounded-xl border border-dashed border-[hsl(220_10%_22%)] bg-[hsl(220_14%_6%)] p-5 flex flex-col justify-center" data-testid="dashboard-coming-soon">
            <Plus className="h-5 w-5 text-[hsl(43_45%_55%)]" />
            <h3 className="font-display text-xl mt-4 text-[hsl(45_15%_82%)]">Tools berikutnya</h3>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Workspace ini terus berkembang. Tools baru untuk masalah bisnis sehari-hari akan muncul di sini.
            </p>
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="rounded-xl border border-border bg-card elev-1">
          <div className="flex items-center justify-between px-5 pt-5">
            <div>
              <div className="eyebrow">Terbaru</div>
              <h3 className="text-[15px] font-semibold mt-1">Perhitungan terakhir</h3>
            </div>
            <Link to="/saved" className="text-xs text-primary hover:underline" data-testid="dashboard-view-all-saved">
              Lihat semua
            </Link>
          </div>
          <div className="p-3" data-testid="dashboard-recent-list">
            {recent === null &&
              [0, 1, 2].map((i) => <Skeleton key={i} className="h-14 m-2 bg-[hsl(220_10%_11%)]" />)}
            {recent && recent.length === 0 && (
              <div className="p-6 text-center">
                <FolderOpen className="h-6 w-6 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">Belum ada perhitungan tersimpan. Mulai dari salah satu tool di atas.</p>
              </div>
            )}
            {recent &&
              recent.slice(0, 6).map((c) => {
                const Icon = ICONS[c.tool] || FolderOpen;
                return (
                  <button
                    key={c.id}
                    onClick={() => nav(`${TOOL_META[c.tool].path}?id=${c.id}`)}
                    className="w-full text-left flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-[hsl(220_10%_10%)] nav-link"
                    data-testid={`dashboard-recent-${c.id}`}
                  >
                    <Icon className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{c.title}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {TOOL_META[c.tool].label} · {c.summary?.headline || "–"}
                      </div>
                    </div>
                    {c.permission !== "owner" && (
                      <Badge variant="outline" className="border-[color:var(--hairline)] text-[10px] text-[hsl(43_60%_66%)]">
                        <Users className="h-3 w-3 mr-1" /> {c.permission === "edit" ? "Edit" : "Lihat"}
                      </Badge>
                    )}
                    <span className="text-[11px] text-muted-foreground num hidden sm:flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {fmtDate(c.updated_at)}
                    </span>
                  </button>
                );
              })}
          </div>
        </div>
        <div className="rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-5 elev-1" data-testid="dashboard-token-card">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="eyebrow">AI Recommendation</span>
          </div>
          <h3 className="font-display text-2xl mt-3">Kuota token bulan ini</h3>
          <div className="num text-3xl mt-4" data-testid="dashboard-token-used">
            {fmtNum(quota?.used || 0)}
            <span className="text-base text-muted-foreground"> / {fmtNum(quota?.limit || 0)}</span>
          </div>
          <Progress value={pct} className="h-2 mt-3 bg-[hsl(220_10%_14%)]" data-testid="token-usage-progress" />
          <div className="flex justify-between text-[11px] text-muted-foreground mt-2">
            <span>Sisa {fmtNum(quota?.remaining || 0)} token</span>
            <span className="num">{quota?.model}</span>
          </div>
          <Link to="/usage" className="inline-flex items-center gap-1 text-xs text-primary mt-5 hover:underline" data-testid="dashboard-usage-link">
            Lihat laporan pemakaian <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="num text-2xl text-foreground">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
